import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, Eye, EyeOff, Loader2, Lock, Fingerprint, BadgeCheck, AlertCircle } from 'lucide-react';
import { useAuth, apiFetch } from '../App';

export default function NINVerification({ onVerified, embedded = false }) {
  const { loadMe, setUser, showToast } = useAuth();
  const [nin, setNin]           = useState('');
  const [showNin, setShowNin]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [verified, setVerified] = useState(false);
  const [ninData, setNinData]   = useState(null);
  const [error, setError]       = useState('');
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!embedded) {
      apiFetch('/api/web/nin/status')
        .then(d => { if (d.verified) { setVerified(true); if (onVerified) onVerified(); } })
        .catch(() => {})
        .finally(() => setChecking(false));
    } else { setChecking(false); }
  }, []);

  async function handleVerify() {
    setError('');
    if (!/^\d{11}$/.test(nin.trim())) return setError('NIN must be exactly 11 digits');
    setLoading(true);
    try {
      const data = await apiFetch('/api/web/auth/verify-nin', { method:'POST', body:{ nin: nin.trim() }});
      setNinData(data.data);
      setVerified(true);
      // Update user state directly to prevent gate from showing again
      setUser(u => u ? ({...u, ninVerified: true}) : u);
      showToast('NIN verified successfully!', 'success');
      if (onVerified) setTimeout(onVerified, 1500);
    } catch(err) { setError(err.message); }
    setLoading(false);
  }

  if (checking) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:24, color:'var(--muted)', fontSize:13 }}>
      <Loader2 size={18} style={{ animation:'spin 1s linear infinite' }}/> Checking status...
    </div>
  );

  if (verified) return (
    <div style={{ background:'rgba(0,197,102,.08)', border:'1px solid rgba(0,197,102,.25)', borderRadius:14, padding:'16px 18px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom: ninData ? 12 : 0 }}>
        <BadgeCheck size={24} color="#00c566" strokeWidth={2}/>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:'#00c566' }}>Identity Verified</div>
          <div style={{ fontSize:11, color:'var(--text2)' }}>Verified via NIMC database</div>
        </div>
      </div>
      {ninData && (
        <div style={{ display:'flex', gap:12, alignItems:'center', paddingTop:12, borderTop:'1px solid rgba(0,197,102,.15)' }}>
          {ninData.photo && (
            <img src={`data:image/jpeg;base64,${ninData.photo}`} alt="NIN"
              style={{ width:52, height:52, borderRadius:10, objectFit:'cover', border:'2px solid rgba(0,197,102,.4)', flexShrink:0 }}/>
          )}
          <div style={{ display:'grid', gap:3 }}>
            {[['Full Name', `${ninData.firstName||''} ${ninData.lastName||''}`.trim()],['Date of Birth', ninData.dob||'—'],['Gender', ninData.gender||'—']].filter(([,v])=>v&&v!=='—').map(([k,v])=>(
              <div key={k} style={{ fontSize:12 }}>
                <span style={{ color:'var(--muted)', marginRight:4 }}>{k}:</span>
                <strong style={{ color:'var(--text)' }}>{v}</strong>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      {!embedded && (
        <>
          {/* Alert banner */}
          <div style={{ background:'rgba(255,107,53,.06)', border:'1px solid rgba(255,107,53,.25)', borderRadius:14, padding:'14px 18px', marginBottom:20, display:'flex', gap:12, alignItems:'flex-start' }}>
            <Shield size={22} color="var(--orange)" strokeWidth={2} style={{ flexShrink:0, marginTop:1 }}/>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:'var(--orange)', marginBottom:3 }}>NIN Verification Required</div>
              <div style={{ fontSize:12, color:'var(--text2)', lineHeight:1.7 }}>
                As required by Nigerian law, all users must verify their National Identification Number (NIN) before accessing NaijaPredict.
              </div>
            </div>
          </div>

          {/* Feature pills */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:20 }}>
            {[
              [Lock,        'Secure & Private', 'Verified via NIMC'],
              [Fingerprint, 'One-time only',    'Verify once, access forever'],
            ].map(([Icon, title, sub]) => (
              <div key={title} style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:12, padding:'12px 14px', display:'flex', gap:10, alignItems:'center' }}>
                <div style={{ width:34, height:34, borderRadius:9, background:'rgba(255,107,53,.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <Icon size={16} color="var(--orange)" strokeWidth={2}/>
                </div>
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--text)' }}>{title}</div>
                  <div style={{ fontSize:10, color:'var(--muted)' }}>{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Input card */}
      <div style={{ background:'var(--bg1)', border:'1px solid var(--border)', borderRadius:16, padding:'20px', overflow:'hidden' }}>

        {/* Card header */}
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, paddingBottom:14, borderBottom:'1px solid var(--border)' }}>
          <div style={{ width:38, height:38, borderRadius:10, background:'linear-gradient(135deg,#ff6b35,#f5c842)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Fingerprint size={20} color="#000" strokeWidth={2.5}/>
          </div>
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:'var(--text)' }}>Enter Your NIN</div>
            <div style={{ fontSize:11, color:'var(--muted)' }}>Dial *346# on your phone to get your NIN</div>
          </div>
        </div>

        {/* NIN input */}
        <label style={{ fontSize:11, color:'var(--text2)', fontWeight:600, letterSpacing:.8, display:'block', marginBottom:8 }}>
          NATIONAL IDENTIFICATION NUMBER
        </label>
        <div style={{ position:'relative', marginBottom:10 }}>
          <input
            type={showNin ? 'text' : 'password'}
            inputMode="numeric"
            placeholder="• • • • • • • • • • •"
            value={nin}
            onChange={e => { setNin(e.target.value.replace(/\D/g,'').slice(0,11)); setError(''); }}
            maxLength={11}
            style={{
              width:'100%', padding:'15px 50px 15px 16px',
              background:'var(--bg2)',
              border:`2px solid ${error?'#ff4444':nin.length===11?'#00c566':nin.length>0?'rgba(255,107,53,.5)':'var(--border)'}`,
              borderRadius:12, color:'var(--text)',
              fontSize:24, letterSpacing:nin.length>0?8:2, textAlign:'center',
              outline:'none', fontFamily:"'DM Mono',monospace",
              transition:'border-color .2s, box-shadow .2s',
              boxShadow: nin.length===11 ? '0 0 0 3px rgba(0,197,102,.12)' : 'none',
            }}
          />
          <button type="button" onClick={() => setShowNin(s=>!s)}
            style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--muted)', display:'flex', alignItems:'center', padding:4 }}>
            {showNin ? <EyeOff size={18} strokeWidth={1.5}/> : <Eye size={18} strokeWidth={1.5}/>}
          </button>
        </div>

        {/* Digit progress */}
        <div style={{ display:'flex', gap:3, marginBottom:12 }}>
          {Array.from({length:11}).map((_,i) => (
            <div key={i} style={{
              flex:1, height:3, borderRadius:2,
              background: i < nin.length ? (nin.length===11 ? '#00c566' : 'var(--orange)') : 'var(--bg3)',
              transition:'background .15s',
            }}/>
          ))}
        </div>

        {/* Status row */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:11, marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:5, color:'var(--muted)' }}>
            <Lock size={10} strokeWidth={2}/> Encrypted & Secure
          </div>
          {nin.length === 11
            ? <div style={{ display:'flex', alignItems:'center', gap:4, color:'#00c566', fontWeight:600 }}>
                <CheckCircle size={12} strokeWidth={2}/> Ready to verify
              </div>
            : <span style={{ color:'var(--muted)' }}>{nin.length} / 11 digits</span>
          }
        </div>

        {/* Error */}
        {error && (
          <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'rgba(255,68,68,.06)', border:'1px solid rgba(255,68,68,.2)', borderRadius:10, marginBottom:14, fontSize:12, color:'#ff4444' }}>
            <AlertCircle size={15} strokeWidth={2} style={{ flexShrink:0 }}/>
            {error}
          </div>
        )}

        {/* Submit button */}
        <button className="btn-primary" onClick={handleVerify}
          disabled={loading || nin.length !== 11}
          style={{ width:'100%', padding:'14px', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center', gap:8, borderRadius:12 }}>
          {loading
            ? <><Loader2 size={17} style={{ animation:'spin 1s linear infinite' }}/> Verifying with NIMC...</>
            : <><Shield size={17} strokeWidth={2}/> Verify My NIN</>
          }
        </button>

        {/* Footer note */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, fontSize:10, color:'var(--muted)', marginTop:12 }}>
          <Lock size={9} strokeWidth={2}/>
          Your NIN is verified via NIMC and never stored on our servers
        </div>
      </div>
    </div>
  );
}
